/**
 * Add Organization Form Schema
 *
 * Defines the validation for organization creation.
 * organizationId and timestamps are auto-generated by the backend.
 *
 * Form metadata (name, title, description) is loaded from form.yml
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

// ============================================================================
// Export Form Metadata from YAML
// ============================================================================

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

// ============================================================================
// Organization Type and Role Enums
// ============================================================================

export const OrganizationTypeEnum = z.enum([
  'CSP',
  'Grower',
  'Agronomist',
  'Robot Manufacturer',
  'Ag Retailer',
  'Ag Co-op',
  'Device or Sensor Manufacturer',
  'Autonomy Service Provider',
  'Application Provider',
  'Software Developer',
])
export type OrganizationType = z.infer<typeof OrganizationTypeEnum>

export const OrganizationRoleEnum = z.enum([
  'Customer',
  'Partner',
  'Supplier',
])
export type OrganizationRole = z.infer<typeof OrganizationRoleEnum>

// ============================================================================
// Form Schema (What user fills in)
// ============================================================================

export const AddOrganizationFormSchema = z.object({
  // Required fields
  name: z
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name cannot exceed 100 characters'),

  // Backend-required fields (Mint Organization)
  orgCode: z
    .string()
    .min(1, 'Org code is required'),

  country: z
    .string()
    .min(2, 'Country is required')
    .max(60, 'Country cannot exceed 60 characters'),

  // Optional to match the backend (type/role are nullable there). Treat null/'' as
  // "not selected" (undefined) so a null value never fails validation.
  type: z.preprocess(
    (v) => (v == null || v === '' ? undefined : v),
    OrganizationTypeEnum.optional(),
  ),

  role: z.preprocess(
    (v) => (v == null || v === '' ? undefined : v),
    OrganizationRoleEnum.optional(),
  ),

  // Optional contact information
  // Backend field name is emailId; required by the API
  emailId: z
    .string()
    .email('Invalid email format')
    .max(254, 'Email cannot exceed 254 characters'),

  // E.164 format e.g. "+12133734253" — stored and sent as-is to API
  phone: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/, 'Phone number is invalid (enter 7-15 digits including the country code)')
    .optional()
    .or(z.literal('')),

  website: z
    .string()
    .url('Invalid URL format')
    .optional()
    .or(z.literal('')),

  // Optional organization details
  taxId: z
    .string()
    .max(50, 'Tax ID cannot exceed 50 characters')
    .optional()
    .or(z.literal('')),

  accountManagerId: z.string().optional().or(z.literal('')),

  employeeCount: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = Number(val)
      return isNaN(num) ? undefined : num
    },
    z.number()
      .int('Employee count must be a whole number')
      .positive('Employee count must be greater than 0')
      .optional()
  ),
})

export type AddOrganizationFormData = z.infer<typeof AddOrganizationFormSchema>

// ============================================================================
// API Request Transformation
// ============================================================================

/**
 * Transform form data to API request format.
 *
 * - Converts empty strings to null for optional fields (never undefined)
 * - Maintains required fields as-is
 * - Filters out fields that are null/undefined in the final payload
 */
export function transformToApiRequest(
  formData: AddOrganizationFormData,
) {
  // Helper to convert empty strings to null (for optional fields)
  const emptyToNull = (value: string | undefined): string | null => {
    return (value && value.trim() !== '') ? value : null
  }

  // Build the API payload
  const payload: Record<string, any> = {
    orgCode: formData.orgCode,
    name: formData.name,
    website: emptyToNull(formData.website),
    country: formData.country,
    emailId: formData.emailId,
    type: emptyToNull(formData.type),
    role: emptyToNull(formData.role),
    phone: emptyToNull(formData.phone),
    taxId: emptyToNull(formData.taxId),
    accountManagerId: emptyToNull(formData.accountManagerId),
    employeeCount: formData.employeeCount,
  }

  // Filter out null/undefined values to avoid sending empty fields
  return Object.fromEntries(
    Object.entries(payload).filter(([_, value]) => value !== null && value !== undefined)
  )
}
